import { useState, type FormEvent } from 'react'
import { CheckCircle2, FileText, Image as ImageIcon, Send } from 'lucide-react'
import type { SupportMessage, SupportTicketDetail } from '@/api/supportApi'
import { Button } from '@/components/ui/button'
import { AttachmentTools, AttachmentTray, FileDropZone, usePendingFiles } from '@/components/support/SupportAttachments'
import { AttachmentViewer } from '@/components/support/AttachmentViewer'
import { useSupportReply } from '@/hooks/useSupport'
import { formatBytes, isImage, linkify } from '@/lib/support/supportFiles'
import { FIXED_BODY, PHI_REMINDER, isClosedForReplies, timeAgo, waitingOnYou } from '@/lib/support/supportView'
import { cn } from '@/lib/utils'

/** Middle pane: the ticket header, the "waiting for you" banner, the thread and the composer. */
export function SupportConversation({
  ticket,
  messages,
  canReply,
  now,
  onReportNew,
}: {
  ticket: SupportTicketDetail
  messages: SupportMessage[]
  canReply: boolean
  now: number
  onReportNew: () => void
}) {
  const reply = useSupportReply(String(ticket.number))
  // Every file on the ticket, in conversation order: the viewer steps through them.
  const files = messages.flatMap((m) => m.attachments)
  const [viewing, setViewing] = useState<number | null>(null)
  const openAttachment = (id: string) => setViewing(Math.max(0, files.findIndex((a) => a.id === id)))
  const pending = usePendingFiles()
  const [body, setBody] = useState('')
  const closed = isClosedForReplies(ticket.status)
  const open = ticket.status !== 'resolved' && !closed
  const title21Replied = messages.some((m) => m.author.kind === 'agent')
  const raisedFrom = ticket.context?.url ? pathOf(ticket.context.url) : null

  const send = (e: FormEvent) => {
    e.preventDefault()
    if (!body.trim() && !pending.files.length) return
    reply.mutate(
      { body: body.trim() || 'Files attached.', files: pending.files },
      {
        onSuccess: () => {
          setBody('')
          pending.clear()
        },
      },
    )
  }

  return (
    <section className='flex min-h-0 flex-col border-r border-border'>
      <header className='flex items-start justify-between gap-4 border-b border-border px-5 py-3'>
        <div className='min-w-0'>
          <div className='font-mono text-[11px] text-muted-foreground'>{ticket.key}</div>
          <h2 className='mt-0.5 text-base font-bold text-foreground'>{ticket.subject}</h2>
          <p className='mt-0.5 text-xs text-muted-foreground'>
            Raised by {ticket.reporter?.name ?? 'your team'} · {new Date(ticket.createdAt).toLocaleString()}
            {raisedFrom ? ` · from ${raisedFrom}` : ''}
          </p>
        </div>
        {canReply && open && title21Replied ? (
          <Button variant='outline' size='sm' disabled={reply.isPending} onClick={() => reply.mutate({ body: FIXED_BODY })}>
            <CheckCircle2 className='h-4 w-4' />
            It's fixed — let Title21 close it
          </Button>
        ) : null}
      </header>

      {waitingOnYou(ticket.status, messages) ? (
        <div className='mx-5 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
          <b>Title21 is waiting for you.</b> Reply to keep the ticket moving — the SLA clock is paused until you answer.
        </div>
      ) : ticket.status === 'waiting_on_customer' ? (
        <div className='mx-5 mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
          <b>Title21 has your answer.</b> They'll pick the ticket up from here.
        </div>
      ) : null}
      {ticket.status === 'resolved' ? (
        <div className='mx-5 mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900'>
          <b>Title21 resolved this.</b> {ticket.resolution?.summary ?? ''} Still not right? Reply and it reopens.
        </div>
      ) : null}

      <ol className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4'>
        <li className='self-center text-[11px] text-muted-foreground'>— Ticket raised · {timeAgo(ticket.createdAt, now)} —</li>
        {messages.map((m) => (
          <Bubble key={m.id} m={m} now={now} onOpen={openAttachment} />
        ))}
      </ol>

      {!canReply ? null : closed ? (
        <div className='border-t border-border px-5 py-3 text-sm text-muted-foreground'>
          This ticket is closed.{' '}
          <button type='button' className='font-medium text-primary hover:underline' onClick={onReportNew}>
            Report a new issue
          </button>{' '}
          to follow up.
        </div>
      ) : (
        <form onSubmit={send} className='border-t border-border px-4 py-3'>
          <FileDropZone pending={pending} className='rounded-lg border border-input bg-background focus-within:ring-2 focus-within:ring-ring'>
            <textarea
              rows={3}
              value={body}
              maxLength={20000}
              onChange={(e) => setBody(e.target.value)}
              placeholder='Reply to Title21… (drop or paste files and screenshots)'
              aria-label='Reply to Title21'
              className='block w-full resize-y rounded-t-lg bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground'
            />
            <div className='flex flex-col gap-2 border-t border-border px-2 py-1.5'>
              <AttachmentTray pending={pending} />
              <div className='flex items-center justify-between gap-2'>
                <AttachmentTools pending={pending} onLink={(url) => setBody((b) => (b.trim() ? `${b.trimEnd()}\n${url}` : url))} />
                <Button type='submit' size='sm' disabled={(!body.trim() && !pending.files.length) || reply.isPending}>
                  <Send className='h-4 w-4' />
                  {reply.isPending ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </div>
          </FileDropZone>
          <p className='mt-1.5 text-xs text-amber-700'>{PHI_REMINDER}</p>
          {reply.error ? <p className='mt-1 text-sm text-destructive'>{(reply.error as Error).message}</p> : null}
        </form>
      )}
      {viewing !== null && files[viewing] ? (
        <AttachmentViewer ticket={String(ticket.number)} files={files} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />
      ) : null}
    </section>
  )
}

function Bubble({ m, now, onOpen }: { m: SupportMessage; now: number; onOpen: (attachmentId: string) => void }) {
  const mine = m.author.kind === 'customer'
  if (m.author.kind === 'system')
    return (
      <li className='self-center rounded-full bg-muted px-3 py-1 text-[11px] text-muted-foreground'>
        {m.body}
      </li>
    )
  return (
    <li className={cn('max-w-[78%] rounded-xl px-3 py-2 text-sm leading-relaxed', mine ? 'self-end rounded-br-sm bg-primary text-primary-foreground' : 'self-start rounded-bl-sm bg-muted text-foreground')}>
      <div className={cn('mb-0.5 text-[11px] font-semibold', mine ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
        {mine ? m.author.name : `${m.author.name} · Title21 Support`} · {timeAgo(m.createdAt, now)}
        {m.edited ? ' · edited' : ''}
      </div>
      {m.body ? (
        <p className='whitespace-pre-wrap break-words'>
          {linkify(m.body).map((s, i) =>
            s.href ? (
              <a key={i} href={s.href} target='_blank' rel='noopener noreferrer' className='underline underline-offset-2'>
                {s.text}
              </a>
            ) : (
              <span key={i}>{s.text}</span>
            ),
          )}
        </p>
      ) : null}
      {m.attachments.length ? (
        <ul className='mt-2 flex flex-wrap gap-1.5'>
          {m.attachments.map((a) => (
            <li key={a.id}>
              <button
                type='button'
                onClick={() => onOpen(a.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                  mine ? 'border-primary-foreground/30 bg-primary-foreground/10 hover:bg-primary-foreground/20' : 'border-border bg-background hover:bg-muted',
                )}
              >
                {isImage(a.mimeType) ? <ImageIcon className='h-3.5 w-3.5' aria-hidden /> : <FileText className='h-3.5 w-3.5' aria-hidden />}
                <span className='max-w-[180px] truncate'>{a.fileName}</span>
                <span className='opacity-70'>{formatBytes(a.sizeBytes)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}
